import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation, computed, input, signal } from '@angular/core';

// Long enough to notice the tick, short enough that a second copy shows a fresh one.
const COPIED_FEEDBACK_MS = 1500;

/** The small action row under a chat message. Copy only for now; regenerate and edit come later. */
@Component({
  selector: 'agent-message-actions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { '[class.pinned]': '$pinned()' },
  template: `
    <div class="message-actions">
      <button type="button" class="message-action copy" [class.copied]="$copied()" [title]="$label()" [attr.aria-label]="$label()" (click)="copy()">
        <i [class]="$copied() ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
      </button>
    </div>
  `,
  styleUrls: ['./message.actions.component.less'],
})
export class AgentMessageActionsComponent implements OnDestroy {

  public $text = input.required<string>({ alias: 'text' });
  /** Keeps the row visible without hovering; set on the latest reply. */
  public $pinned = input<boolean>(false, { alias: 'pinned' });

  protected readonly $copied = signal(false);
  protected readonly $label = computed(() => this.$copied() ? 'Copied' : 'Copy');
  private resetTimer: ReturnType<typeof setTimeout> | undefined;

  public ngOnDestroy(): void {

    clearTimeout(this.resetTimer);
  }

  protected async copy(): Promise<void> {

    try {
      await navigator.clipboard.writeText(this.$text());
    } catch {
      // A refused clipboard leaves the copy icon as it was, which is the honest signal that nothing copied.
      return;
    }

    this.$copied.set(true);
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.$copied.set(false), COPIED_FEEDBACK_MS);
  }
}
